var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import React from 'react';
import { isCoordinates } from '../utils';
/**
 * ArcGIS Autocomplete search component for location suggestions.
 *
 * @param {ArcgisAutocompleteProps} root0 - Props for the component.
 * @param {string} root0.label - Label text for the search input placeholder.
 * @param {object} root0.gisClient - GIS client instance for fetching location suggestions.
 * @param {function(string | undefined): void} root0.onChange - Callback fired when a value is selected or cleared.
 * @param {string} [root0.initialInputValue] - Initial value for the search input.
 * @param {React.ReactNode} [root0.startAdornment] - Optional adornment to render at the start of the input.
 * @param {boolean} [root0.autoFocus] - Whether the input should auto-focus on mount.
 * @param {boolean} [root0.readOnly] - If true, the input will be read-only.
 * @param {boolean} [root0.error] - Whether the input is in an error state.
 * @param {boolean} [root0.disabled] - Whether the input is disabled.
 * @param {boolean} [root0.required] - Whether the input is required.
 * @param {boolean} [root0.editMode] - If true, prevents focusing via tab navigation.
 * @param {string} [root0.ariaDescribedBy] - ARIA descriptor ID for accessibility.
 *
 * @returns {React.ReactElement} The ArcGIS autocomplete search component.
 */
export const ArcgisAutocompleteSearch = ({ label, gisClient, onChange, initialInputValue, startAdornment, autoFocus, readOnly, error, disabled, required, editMode, ariaDescribedBy, }) => {
    const [loading, setLoading] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(() => initialInputValue !== null && initialInputValue !== void 0 ? initialInputValue : '');
    const [options, setOptions] = React.useState([]);
    const debounceFetchRef = React.useRef(undefined);
    const debouncedFetchSuggestions = (input) => {
        setLoading(true);
        if (debounceFetchRef.current) {
            window.clearTimeout(debounceFetchRef.current);
        }
        if (input.trim() === '') {
            setLoading(false);
            setOptions([]);
            return;
        }
        debounceFetchRef.current = window.setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (isCoordinates(input)) {
                    setOptions([input]);
                }
                else {
                    const suggestions = yield gisClient.suggest({ text: encodeURIComponent(input) });
                    /* eslint-disable @typescript-eslint/no-explicit-any */
                    setOptions(suggestions.suggestions.map((suggestion) => suggestion.text));
                }
            }
            catch (error) {
                console.error('Error fetching places:', error);
                setOptions([]);
            }
            finally {
                setLoading(false);
            }
        }), 800);
    };
    return (React.createElement(Autocomplete, { freeSolo: true, disabled: disabled, autoFocus: autoFocus, readOnly: readOnly, options: options, inputValue: inputValue, loading: loading, slotProps: {
            clearIndicator: {
                onClick: () => {
                    if (inputValue) {
                        setInputValue('');
                    }
                    setOptions([]);
                    onChange(undefined);
                },
            },
        }, filterOptions: (x) => x, onInputChange: (event, newInputValue) => {
            // onChange event also triggers this event
            setInputValue(newInputValue);
            debouncedFetchSuggestions(newInputValue);
        }, onChange: (event, value) => {
            onChange(value);
        }, renderOption: (props, option) => {
            return (React.createElement(Box, Object.assign({ component: 'li' }, props, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    gap: '8px',
                } }),
                React.createElement(PlaceOutlinedIcon, { sx: { fontSize: 20 } }),
                React.createElement(Typography, { variant: 'body1' }, option)));
        }, renderInput: (params) => {
            return (React.createElement(TextField, { error: error, InputLabelProps: params.InputLabelProps, autoFocus: autoFocus, required: required, id: params.id, inputProps: Object.assign(Object.assign({}, params.inputProps), { tabIndex: editMode ? -1 : undefined }), fullWidth: params.fullWidth, size: params.size, disabled: params.disabled, variant: 'outlined', InputProps: Object.assign(Object.assign({}, params.InputProps), { readOnly: readOnly, startAdornment: startAdornment, 'aria-describedby': ariaDescribedBy }), placeholder: label }));
        } }));
};
